import { useEffect, useState } from 'react';

// Direct paths, not the application barrel: this file is inside it, and importing it from itself
// is a cycle (see CasPendingEmailsPill in CasContextProviders).
import { DEFAULT_KEY } from './BatchRowList';
import { IBatchRow } from './batchRow';

// Which rows the message list shows. Invalid and Excluded are offered only while they have rows.
export type BatchListViewType = 'all' | 'invalid' | 'excluded';

// Named as the toggle names each view. Excluded rows are "Skip" to the sender.
const NAV_LABELS: Record<BatchListViewType, string> = {
  all: 'Msg',
  invalid: 'Invalid',
  excluded: 'Skip',
};

interface Args {
  rows: IBatchRow[];
  included: Set<string>;
  // Exactly the rows the footer counts as invalid, so the toggle and the footer never disagree.
  invalidKeys: Set<string>;
  selectedKey: string;
  // The dialog's stable select handler - it commits the open row before switching.
  onSelect: (key: string) => void;
}

/*
  Narrows a batch's message list to the rows that need looking at, so a sender with hundreds of rows
  does not have to scroll for the red triangles (or for the rows they unchecked).

  A filtered view lists its matching rows plus the OPEN row. That one rule is what lets a row be
  fixed in place: once valid it no longer matches, but it stays on screen - its warning gone - until
  the sender moves on, and only then leaves the list. Removing it the moment it became valid would
  yank the row being edited out from under them.
*/
export default function useBatchListView({
  rows,
  included,
  invalidKeys,
  selectedKey,
  onSelect,
}: Args) {
  const [listView, setListView] = useState<BatchListViewType>('all');

  const invalidCount = invalidKeys.size;
  const excludedCount = rows.length - included.size;

  const inView = (key: string, view: BatchListViewType) => {
    if (view === 'invalid') return invalidKeys.has(key);
    if (view === 'excluded') return !included.has(key);
    return true;
  };

  /*
    Back to All once the view has nothing left in it - the last row fixed, or taken out - with that
    row still selected. A real reset rather than a derived view: derived, a row turning invalid again
    later would pull the list back into Invalid without the sender asking.
  */
  const viewCount = { all: null, invalid: invalidCount, excluded: excludedCount }[listView];
  useEffect(() => {
    if (viewCount === 0) setListView('all');
  }, [viewCount]);

  // Opens the view's first row, so the right pane shows the problem straight away - unless the open
  // row is already one of them. The default message is never in a filtered view.
  const changeListView = (next: BatchListViewType) => {
    setListView(next);

    if (next === 'all' || (selectedKey !== DEFAULT_KEY && inView(selectedKey, next))) return;

    const first = rows.find((row) => inView(row.key, next));
    if (first) onSelect(first.key);
  };

  const visibleRows =
    listView === 'all'
      ? rows
      : rows.filter((row) => inView(row.key, listView) || row.key === selectedKey);

  // The prev/next arrows walk what the list shows: the default message, then the visible rows.
  const navigableKeys = [DEFAULT_KEY, ...visibleRows.map((row) => row.key)];

  /*
    The default message is not the first of anything - it is the one every message falls back to -
    so it is named rather than numbered, and the messages count from one without it. "2 of 5" made
    the sender do that subtraction themselves to answer "which row am I on, out of how many".
  */
  const formatNavLabel = (index: number, total: number) =>
    index === 0 ? 'Default' : `${NAV_LABELS[listView]} ${index} of ${total - 1}`;

  return {
    listView,
    changeListView,
    visibleRows,
    invalidCount,
    excludedCount,
    navigableKeys,
    formatNavLabel,
  };
}
